// src/components/Shared/RegistrationPage.jsx
// Registration screen aligned to Figma auth layout

import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { IconEye, IconEyeOff } from './FigmaIcons';
import BackButton from './BackButton';
import TermsModal from './TermsModal';
import {
	captureAttributionFromLocation,
	getAttribution,
	identifyUser,
	trackEvent,
	trackPageView,
} from '../../utils/analytics';

// Mirrors server-side policy in server/utils/passwordPolicy.js: ≥5 chars,
// at least one uppercase, at least one digit. Relaxed from the original
// 10-char policy after tester feedback (Stratospia chat, 2026-05).
const isStrongPassword = (value) =>
	typeof value === 'string' &&
	value.length >= 5 &&
	/[A-Z]/.test(value) &&
	/\d/.test(value);

const RegistrationPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { login } = useContext(AuthContext);

	const [formData, setFormData] = useState({
		name: '',
		email: '',
		password: '',
		confirmPassword: '',
	});
	const [rememberMe, setRememberMe] = useState(false);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [showTerms, setShowTerms] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');
	const [loading, setLoading] = useState(false);

	const { name, email, password, confirmPassword } = formData;
	const intent = new URLSearchParams(location.search).get('intent') || '';

	useEffect(() => {
		captureAttributionFromLocation(location.search);
		trackPageView('registration_page', { intent: intent || 'none' });
	}, [location.search, intent]);

	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		// Same defence as LoginPage: autofill and password managers can write
		// into the fields without firing React's onChange, leaving state empty
		// behind visibly filled inputs — which made this handler's own "Please
		// fill in all fields." fire at a filled-looking form. The DOM is the
		// truth at submit time. These consts shadow the state destructure
		// above, so the whole submit path (validation, register call, the
		// auto-login after it) uses the reconciled values.
		const fields = e.currentTarget.elements;
		const domValue = (fieldName, fallback) => {
			const el = fields.namedItem(fieldName);
			return typeof el?.value === 'string' ? el.value : fallback;
		};
		const name = domValue('name', formData.name).trim();
		const email = domValue('email', formData.email).trim();
		const password = domValue('password', formData.password);
		const confirmPassword = domValue('confirmPassword', formData.confirmPassword);
		setFormData({ name, email, password, confirmPassword });
		setErrorMsg('');

		if (!name || !email || !password || !confirmPassword) {
			setErrorMsg('Please fill in all fields.');
			return;
		}
		if (password !== confirmPassword) {
			setErrorMsg('Passwords do not match.');
			return;
		}
		if (!isStrongPassword(password)) {
			setErrorMsg('Password must be at least 5 characters with one uppercase letter and one number.');
			return;
		}
		if (!acceptedTerms) {
			setErrorMsg('You need to accept the terms and conditions.');
			return;
		}

		setLoading(true);
		try {
			// Persist attribution alongside the new account so server-side
			// reports + future retargeting know which channel produced this
			// user. The User model accepts an attribution object; PostHog
			// also gets it via identifyUser below for funnel analysis.
			const attribution = getAttribution();
			const registerRes = await api.post('/auth/register', {
				name,
				email,
				password,
				attribution: Object.keys(attribution).length > 0 ? attribution : undefined,
			});
			await login(email, password);
			if (rememberMe) {
				localStorage.setItem('eventory.rememberAuth', '1');
			}
			const newUserId = registerRes.data?.user?._id || registerRes.data?._id;
			if (newUserId) {
				identifyUser(newUserId, {
					name,
					email,
					signup_intent: intent || 'none',
					...(Object.keys(attribution).length > 0 ? { signup_attribution: attribution } : {}),
				});
			}
			trackEvent('registration_success', {
				intent: intent || 'none',
				remember_me: rememberMe,
			});
			// First-time users land on the welcome screen (sample vs real event).
			// If they came in via an invite link or other intent flow, honour
			// that origin instead.
			const from = location.state?.from?.pathname || '/welcome';
			navigate(from, { replace: true });
		} catch (error) {
			trackEvent('registration_failed', {
				intent: intent || 'none',
				reason: error.response?.data?.msg || 'unknown_error',
			});
			setErrorMsg(error.response?.data?.msg || 'Registration failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="ev-page ev-auth-page">
			<div className="ev-auth-card">
				<div className="ev-back-row">
					<BackButton to="/login" label="Back to sign in" />
				</div>
				<div className="ev-auth-head">
					<div className="ev-auth-logo">
						<img src="/images/eventory-logo.svg" alt="Eventory" />
					</div>
					<h1 className="ev-auth-title">Welcome to Eventory</h1>
					<p className="ev-auth-subtitle">Create your account to get started.</p>
					{intent === 'demo' && (
						<p className="ev-inline-note">You came from a demo request. Welcome.</p>
					)}
					{intent === 'self_serve' && (
						<p className="ev-inline-note">Let&apos;s set up your account.</p>
					)}
				</div>

				{errorMsg && <div className="ev-alert ev-alert-danger">{errorMsg}</div>}

				<form onSubmit={handleSubmit} className="ev-auth-grid">
					<div className="ev-auth-field">
						<label htmlFor="name">Name</label>
						<div className="ev-auth-input-wrap">
							<input
								type="text"
								id="name"
								name="name"
								autoComplete="name"
								className="ev-auth-input"
								value={name}
								onChange={handleChange}
								placeholder="e.g., John Doe"
								required
							/>
						</div>
					</div>

					<div className="ev-auth-field">
						<label htmlFor="email">Email Address</label>
						<div className="ev-auth-input-wrap">
							<input
								type="email"
								id="email"
								name="email"
								autoComplete="email"
								className="ev-auth-input"
								value={email}
								onChange={handleChange}
								placeholder="you@example.com"
								required
							/>
						</div>
					</div>

					<div className="ev-auth-field">
						<label htmlFor="password">Password</label>
						<div className="ev-auth-input-wrap">
							<input
								type={showPassword ? 'text' : 'password'}
								id="password"
								name="password"
								autoComplete="new-password"
								className="ev-auth-input"
								value={password}
								onChange={handleChange}
								placeholder="Strong password"
								aria-describedby="password-hint"
								required
							/>
							<button
								type="button"
								className="ev-auth-eye"
								onClick={() => setShowPassword((prev) => !prev)}
								aria-label={showPassword ? 'Hide password' : 'Show password'}
							>
								{showPassword ? <IconEyeOff className="ev-icon-svg" /> : <IconEye className="ev-icon-svg" />}
							</button>
						</div>
						<p id="password-hint" className="ev-auth-hint">
							5+ characters · one uppercase · one number
						</p>
					</div>

					<div className="ev-auth-field">
						<label htmlFor="confirmPassword">Confirm Password</label>
						<div className="ev-auth-input-wrap">
							<input
								type={showConfirmPassword ? 'text' : 'password'}
								id="confirmPassword"
								name="confirmPassword"
								autoComplete="new-password"
								className="ev-auth-input"
								value={confirmPassword}
								onChange={handleChange}
								placeholder="Repeat your password"
								required
							/>
							<button
								type="button"
								className="ev-auth-eye"
								onClick={() => setShowConfirmPassword((prev) => !prev)}
								aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
							>
								{showConfirmPassword ? <IconEyeOff className="ev-icon-svg" /> : <IconEye className="ev-icon-svg" />}
							</button>
						</div>
					</div>

					<div className="ev-check-row is-left">
						<label className="ev-check" htmlFor="rememberSignUp">
							<input
								type="checkbox"
								id="rememberSignUp"
								checked={rememberMe}
								onChange={(e) => setRememberMe(e.target.checked)}
							/>
							<span>Remember me</span>
						</label>
					</div>

					<div className="ev-check-row is-left">
						<label className="ev-check" htmlFor="terms">
							<input
								type="checkbox"
								id="terms"
								checked={acceptedTerms}
								onChange={(e) => setAcceptedTerms(e.target.checked)}
							/>
							<span>
								By signing up, you agree with our{' '}
								<button
									type="button"
									className="ev-inline-link"
									onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
									style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
								>
									terms and conditions
								</button>
							</span>
						</label>
					</div>

					{showTerms && (
						<TermsModal
							onClose={() => setShowTerms(false)}
							onAccept={() => { setAcceptedTerms(true); setShowTerms(false); }}
						/>
					)}

					<button type="submit" className="ev-auth-primary" disabled={loading}>
						{loading ? 'Creating account...' : 'Sign Up'}
					</button>

					<p className="ev-auth-switch">
						Already have an account? <Link to="/login" className="ev-inline-link">Log in</Link>
					</p>
				</form>
			</div>
		</div>
	);
};

export default RegistrationPage;
